import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { ArrowLeft, CheckCircle, Circle } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'

type ProfessionalReviewStepProps = {
  onBack: () => void
  onComplete: () => Promise<void>
  professional: {
    hasProfile: boolean
    displayName: string | null
    publicationStatus: string | null
    assignmentsCount: number
    availabilityWindowsCount: number
  } | null
}

export function ProfessionalReviewStep({ onBack, onComplete, professional }: ProfessionalReviewStepProps) {
  const { t } = useTranslation('onboarding')
  const [completing, setCompleting] = useState(false)

  const items = [
    {
      key: 'profile',
      done: Boolean(professional?.hasProfile),
      label: t('professional.review.items.profile'),
      detail: professional?.displayName ?? undefined,
    },
    {
      key: 'services',
      done: (professional?.assignmentsCount ?? 0) > 0,
      label: t('professional.review.items.services'),
      detail:
        (professional?.assignmentsCount ?? 0) > 0
          ? t('professional.review.servicesCount', { count: professional?.assignmentsCount ?? 0 })
          : t('professional.review.servicesPending'),
    },
    {
      key: 'availability',
      done: (professional?.availabilityWindowsCount ?? 0) > 0,
      label: t('professional.review.items.availability'),
      detail:
        (professional?.availabilityWindowsCount ?? 0) > 0
          ? t('professional.review.windowsCount', { count: professional?.availabilityWindowsCount ?? 0 })
          : t('professional.review.availabilityPending'),
    },
  ]

  const handleComplete = async () => {
    try {
      setCompleting(true)
      await onComplete()
      toast.success(t('professional.review.completed'))
    } catch {
      toast.error(t('professional.review.error'))
    } finally {
      setCompleting(false)
    }
  }

  return (
    <div className="flex flex-col px-4 py-6 animate-in fade-in duration-500">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-[#F08A24] to-[#f9a84c] mb-4">
          <CheckCircle weight="bold" className="w-7 h-7 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-[#0B2A4A] dark:text-white mb-2">
          {t('professional.review.title')}
        </h2>
        <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
          {t('professional.review.subtitle')}
        </p>
      </div>

      <div className="max-w-lg mx-auto w-full space-y-3">
        {items.map((item) => (
          <Card key={item.key} className={cn(item.done && 'border-green-500/50')}>
            <CardContent className="flex items-center gap-3 p-4">
              {item.done ? (
                <CheckCircle weight="fill" className="w-6 h-6 text-green-500 shrink-0" />
              ) : (
                <Circle className="w-6 h-6 text-gray-300 dark:text-gray-600 shrink-0" />
              )}
              <div className="min-w-0">
                <p className="font-medium text-gray-800 dark:text-gray-100">{item.label}</p>
                {item.detail && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{item.detail}</p>
                )}
              </div>
            </CardContent>
          </Card>
        ))}

        <p className="text-xs text-gray-400 dark:text-gray-500 text-center pt-2">
          {t('professional.review.publicationHint')}
        </p>
      </div>

      <div className="flex items-center justify-between mt-10 max-w-lg mx-auto w-full">
        <Button
          onClick={onBack}
          variant="ghost"
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400"
        >
          <ArrowLeft weight="bold" className="mr-2 w-4 h-4" />
          {t('back')}
        </Button>

        <Button
          onClick={handleComplete}
          disabled={completing}
          className="bg-[#F08A24] hover:bg-[#e07a14] text-white"
        >
          {completing ? t('completing') : t('professional.review.cta')}
        </Button>
      </div>
    </div>
  )
}
